// The ringtone is synthesised, so the portal ships no audio asset.
const RING_FREQUENCIES_HZ = [440, 480];
const RING_BURSTS = 2;
const RING_BURST_SECONDS = 0.4;
const RING_BURST_GAP_SECONDS = 0.2;
const RING_REPEAT_MS = 3000;
const RING_VOLUME = 0.08;
const GESTURE_EVENTS = ["pointerdown", "keydown"] as const;

let context: AudioContext | null = null;
let ringing = false;
let timer: number | null = null;
let sounding: OscillatorNode[] = [];

const audioContext = (): AudioContext | null => {
  if (context) return context;
  if (typeof window === "undefined" || typeof window.AudioContext === "undefined") return null;
  try {
    context = new window.AudioContext();
  } catch {
    // No audio: the notification and the panel still show the call.
    context = null;
  }
  return context;
};

const resume = (audio: AudioContext) => {
  if (audio.state !== "suspended") return;
  // Browsers keep audio blocked until the agent interacted with the page; then the call rings silently.
  audio.resume().catch(() => undefined);
};

const forget = (oscillator: OscillatorNode) => {
  sounding = sounding.filter(active => active !== oscillator);
};

const playTone = (audio: AudioContext, output: AudioNode, frequency: number, start: number, stop: number) => {
  const oscillator = audio.createOscillator();
  oscillator.frequency.value = frequency;
  oscillator.connect(output);
  oscillator.start(start);
  oscillator.stop(stop);
  oscillator.onended = () => forget(oscillator);
  sounding.push(oscillator);
};

const playBursts = (audio: AudioContext) => {
  for (let burst = 0; burst < RING_BURSTS; burst += 1) {
    const start = audio.currentTime + burst * (RING_BURST_SECONDS + RING_BURST_GAP_SECONDS);
    const stop = start + RING_BURST_SECONDS;
    const gain = audio.createGain();
    gain.gain.setValueAtTime(RING_VOLUME, start);
    gain.gain.setValueAtTime(0, stop);
    gain.connect(audio.destination);
    RING_FREQUENCIES_HZ.forEach(frequency => playTone(audio, gain, frequency, start, stop));
  }
};

const ring = () => {
  const audio = audioContext();
  if (!audio) return;
  resume(audio);
  if (audio.state === "running") playBursts(audio);
};

/**
 * Lets the first pointer or key interaction with the page create and resume the audio context,
 * because browsers block audio before a gesture. Returns the cleanup.
 */
export const armRingtone = (): (() => void) => {
  const unlock = () => {
    const audio = audioContext();
    if (!audio) return;
    resume(audio);
  };
  GESTURE_EVENTS.forEach(eventName => window.addEventListener(eventName, unlock, { once: true }));
  return () => GESTURE_EVENTS.forEach(eventName => window.removeEventListener(eventName, unlock));
};

/** Rings until stopRingtone is called. */
export const startRingtone = () => {
  if (ringing) return;
  ringing = true;
  ring();
  timer = window.setInterval(ring, RING_REPEAT_MS);
};

export const stopRingtone = () => {
  ringing = false;
  if (timer !== null) window.clearInterval(timer);
  timer = null;
  const silenced = sounding;
  sounding = [];
  silenced.forEach(oscillator => {
    try {
      oscillator.stop();
    } catch {
      // Already finished.
    }
  });
};
